import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { filter, map, mergeMap, catchError } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { AntenneService } from '../../antenne/services/antenne.service';
import { AntenneTLR } from '../../antenne/entities/antenne-tlr.entity';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';

import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';

import { ParcService } from '../services/parc.service';
import { ParcTLR } from '../entities/parc-tlr.entity';

import { UserService } from '../../../humain/user/services/user.service';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { ParcTechnologie } from '../../../../static-types/entities/parc-technologie.entity';
import { ParcMaintenance } from '../../../../static-types/entities/parc-maintenance.entity';

import { UpdateParcOrder } from '../entities/update-parc-order.entity';
import { CreateParcOrder } from '../entities/create-parc-order.entity';
import { ParcBase } from '../entities/parc-base.entity';

import { applicationConstants, couleurIndicateurColors } from '../../../../../app/app.constants'
import { IndicateurCassini } from '../entities/indicateur-cassini.entity';
import { ParcIndicateurOrder } from "../entities/parc-indicateur-order.entity";

type IntervenantData = {
	id: number,
	label: string,
	telephone: string,
	email: string,
}

type CandidatData = {
	id: number,
	label: string,
}

type Option<T> = {
	value: T,
	label: string,
}

type IndicateurFormData = {
	code: number,
	nom: string,
	icon: string,
	surveillable: boolean,
	id: number | null,
	indicateurId: number | null,
	surveille: string,  
	couleur: string,     
	regle: string,
};

@Component({
	selector: 'edit-parc',
	templateUrl: 'edit-parc.html',
	styleUrls: ['edit-parc.css'],
})
export class EditParcComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	title!: string;
	intervenantLabelGetter = (intervenant: IntervenantData) => intervenant.label;
	userLabelGetter = (candidat: CandidatData) => candidat.label;
	allowsCacInput!: boolean;
	waitingForServer = false;
	static readonly HYPERVISEUR_DEFAULT = 'Autre';
	
	hyperviseurOptions: Option<string>[] = [
		{
			value: 'OMI',
			label: 'OMI',
		},
		{
			value: 'SunXper',
			label: 'SunXper',
		},
		{
			value: 'Autre',
			label: 'Autre',
		},
	];
	
	nivIncendieOptions: Option<number| null>[] = [
		{
			value: null,
			label: '',
		},
		{
			value: 1,
			label: '1',
		},
		{
			value: 2,
			label: '2',
		},
		{
			value: 	3,
			label: '3',
		},
		{
			value: 4,
			label: '4',
		},
		{
			value: 5,
			label: '5',
		},
	];
	
	vigilanceIncendieOptions: Option<string | null>[] = [
		{
			value: null,
			label: '',
		},
		{
			value: '0',
			label: 'Non',
		},
		{
			value: '1',
			label: 'Oui',
		}
	]
	
	static readonly INDICATEURS_CODES: number[] = [99, 1, 2, 4, 3, 5, 6, 7, 98];
	static readonly INDICATEUR_SURVEILLABLE = 99;

	private static readonly INDICATEURS_TEMPLATE = EditParcComponent.INDICATEURS_CODES
		.map((code) => ({
			code: code,
			nom: applicationConstants.indicateurs[code]?.nom ?? '',
			icon: applicationConstants.indicateurs[code]?.icon ?? '',
			surveillable: code === EditParcComponent.INDICATEUR_SURVEILLABLE,
		}))
		.filter((indicateur) => indicateur.nom !== '');
		
	indicateursAffiches: IndicateurFormData[] = [];

	couleurLabels: string[] = ['', 'VERT', 'JAUNE', 'ORANGE', 'ROUGE', 'NOIR'];
	
	private static readonly COULEUR_CODES: { [label: string]: number } = {
		'VERT': 1,
		'JAUNE': 2,
		'ORANGE': 3,
		'ROUGE': 4,
		'NOIR': 5,
	};
	
	// Parc en concerne
	parc!: ParcTLR;

	// Listes de valeurs
	antennes!: AntenneTLR[];
	energies!: ParcTechnologie[];
	typesMaintenances!: ParcMaintenance[];
	intervenantsInternesOptions: IntervenantData[] | null = null;
	candidatsCac: CandidatData[] | null = null;

	// Saisies dynamiques
	antenneId$ = new ReplaySubject<number | null>(1);
	responsable$ = new ReplaySubject<IntervenantData | null>(1);
	cac$ = new BehaviorSubject<CandidatData | null>(null);
	
	constructor( //
		private dialog: MatDialog,

		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) intervenantService: IntervenantService,
		@Inject(ParcService) private parcService: ParcService,
		@Inject(UserService) userService: UserService,

		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		/////////////////////////////////////
		// Prechargement des donnees utiles

		let antennes$ = antenneService.getAntenneTLRList();

		let energies$ = staticTypeService.getParcTechnologieList();

		let typesMaintenances$ = staticTypeService.getParcMaintenanceList();

		let entreprisesByIds$ = entrepriseService.getActiveEntreprisesTLRMapById();

		let preloadedData$ = forkJoin({
			antennes: antennes$,
			energies: energies$,
			typesMaintenances: typesMaintenances$,
			entreprisesByIds: entreprisesByIds$,
		});

		preloadedData$.subscribe(
			(preloadedData) => {
				// Memoriser les listes de valeurs chargees
				this.antennes = preloadedData.antennes;
				this.energies = preloadedData.energies;
				this.typesMaintenances = preloadedData.typesMaintenances;
			}
		)

		///////////////////////////////////////
		// Reactions aux saisies utilisateurs

		// Determiner la liste des intervenants possibles en fonction de l'antenne
		let intervenantsInternes$ = intervenantService.getActiveIntervenantsTLR();

		// Preparer les libelles des intervenants a l'avance pour que le moteur de binding ne les recalcule pas en boucle
		let intervenantsInternesData$ = combineLatest([entreprisesByIds$, intervenantsInternes$]).pipe(
			map(
				([entreprisesByIds, intervenantsInternes]) => intervenantsInternes.map(
					(intervenant) => {
						let entepriseId = intervenant.entrepriseId;
						let entreprise = entepriseId === null ? undefined : entreprisesByIds.get(entepriseId);
						let nomEntreprise = entreprise === undefined ? '' : ('(' + entreprise.nom + ')');

						let label = [intervenant.nom, intervenant.prenom, nomEntreprise].join(' ');
						let data = {
							id: intervenant.id,
							label: label,
							telephone: [intervenant.indicatifTelephone, intervenant.telephone].join(' '),
							email: intervenant.email,
						} as IntervenantData;

						return data;
					}
				)
			)
		);

		intervenantsInternesData$.subscribe(
			(data) => {
				this.intervenantsInternesOptions = data;

				if (this.parc.responsableId != null) {
					let responsableIndex = data.findIndex($ => $.id === this.parc.responsableId);
					if (responsableIndex < 0) {
						this.responsable$.next(null);
					} else {
						let responsable = data[responsableIndex];
						this.responsable$.next(responsable);
					}
				}
			}
		);

		// Les CAC possibles dependent de l'antenne
		let candidatsCac$ = this.antenneId$.pipe(
			filter(
				// Bloque le chargement de candidats si le champ n'est pas proposable
				(_) => this.allowsCacInput
			),
			mergeMap(
				(antenneId) => antenneId == null ? of([]) : userService.listActiveCACTLRByAntennes([antenneId])
			)
		);

		let candidatCacData$ = candidatsCac$.pipe(
			map(
				(candidats) => candidats.map(
					(candidat) => ({
						id: candidat.id,
						label: ['(' + candidat.role + ')', candidat.firstName, candidat.lastName].join(' '),
					} as CandidatData)
				)
			)
		);

		candidatCacData$.subscribe(
			(candidats) => this.candidatsCac = candidats
		);

		// Quand l'antenne est modifiee, mettre a jour le parc en consequence		
		this.antenneId$.subscribe(
			(antenneId) => this.parc.antenneId = antenneId as number
		);

		// Quand un intervenant est selectionne en tant que responsable du parc, mettre a jour le parc en consequence
		this.responsable$.subscribe(
			(responsableData) =>  {
				this.parc.responsableId = responsableData ? responsableData.id : null;
			}
		);

		///////////////////////////////////////////////////
		// Initialisation de la vue lors de son affichage

		let parcData$ = this.editParcSubject$.pipe(
			mergeMap(
				(idParc) => idParc == null
					? of([null, [] as IndicateurCassini[]] as [ParcTLR | null, IndicateurCassini[]])
					: combineLatest([
						parcService.getParcTLRById(idParc),
						parcService.getParcIndicateurs(idParc).pipe(
							catchError(() => of([] as IndicateurCassini[]))
						),
					])
			)
		);

		combineLatest([preloadedData$, parcData$]).subscribe(
			([_preloadedData, [parc, indicateurs]]) => {
				// NB: preloadedData$ n'est demande que pour bloquer l'affichage
				// tant que les pre-chargements de donnees de fond ne sont pas
				// termines
				
				if (parc == null) {
					// Cas de la creation d'un nouveau parc
					this.title = "Création d'un parc";

					// Initialiser les valeurs					
					this.parc = new ParcTLR();
					this.parc.tarifNegatif = false;
					this.parc.hyperviseur = EditParcComponent.HYPERVISEUR_DEFAULT;
					this.allowsCacInput = false;
					this.antenneId$.next(null);
				} else {
					// Cas de la modification d'un parc existant
					this.title = "Modification d'un parc";
					
					// Creer une copie du parc pour ne pas alterer les donnees de l'original
					// lors des saisies, avant que l'utilisateur ne valide celles-ci
					let parcCopie = {...parc} as ParcTLR;
					
					// NB: l'original etant une copie d'un ParcWithGroupementDTO, la copie contient un
					// attribut groupementId en trop pour le type ParcTLR, qu'il faut retirer pour
					// prevenir des problemes de mapping ulterieur cote serveur
					delete (parcCopie as any).groupementId;
					
					if (parcCopie.energie === 'Solaire' && !parcCopie.hyperviseur) {
						parcCopie.hyperviseur = EditParcComponent.HYPERVISEUR_DEFAULT;
					}
					
					// Inserer ses donnees dans la vue
					this.parc = parcCopie;
					this.allowsCacInput = !parc.actif;
					this.antenneId$.next(parc.antenneId);
				}
				this.buildIndicateurs(indicateurs);
				
				// Aficher la vue
				this.displaySubject.next(true);
			}
		);
	}

	@Input() id = 'editParcDialog';

	// Liaison de l'observable servant a commander l'affichage du composant
	private editParcSubject$ = new SubjectRelay<number | null>();
	@Input() set editParcCommands(obs: Observable<number | null> | undefined) {
		this.editParcSubject$.registerSource(obs);
	}

	// Evenements de validation de l'operation en cours
	@Output() parcsChanged = new EventEmitter<void>();

	onEnergieChange(energie: string) {
		if (energie != 'Solaire') {
				this.parc.typeOnduleur = null;
				this.parc.hyperviseur = null;
			} else if (!this.parc.hyperviseur) {
				this.parc.hyperviseur = EditParcComponent.HYPERVISEUR_DEFAULT;
			}
	}
	
	onResponsableChange(responsable: IntervenantData) {
		this.responsable$.next(responsable);
	}
	
	onSaveRequested() {
		// Validation des champs
		let errors: string[] = [];
		if (!this.parc.code) {
			errors.push("Vous devez fournir un code pour le parc.");
		}
		if (!this.parc.description) {
			errors.push("Vous devez fournir un nom pour le parc.");
		}
		if (!this.parc.antenneId) {
			errors.push("Vous devez fournir l'antenne du parc.");
		}
		
		if (errors.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Enregistrement impossible',
					messages: errors
				}
			});

			return;
		}
		
		if (this.parc.id) {
			// Cas de la modification d'un parc existant
			let parcTLR = this.parc as ParcTLR;
			let order = {...parcTLR} as UpdateParcOrder;
			if (order.energie !== 'Solaire') {
				order.typeOnduleur = null;
				order.hyperviseur = null;
			}
			if (this.allowsCacInput && order.actif) {
				order.cacId = this.cac$.getValue()?.id;
			}
			order.indicateursCassini = this.buildIndicateursOrder();
			
			this.parcService.updateWithCac(order).subscribe(
				(_id) => {
					this.waitingForServer = false;
					this.displaySubject.next(false);
	
					// Signaler l'evenement de modification au parent			
					this.parcsChanged.emit();
				},
				(error: HttpErrorResponse) => {
					this.waitingForServer = false;
					this.dialog.open(MessageDialogComponent, {
						width: '400px',
						data: {
							title: "Erreur lors de la modification du parc",
							messages: ['Le serveur a rencontré une erreur imprévue']
						}
					});
					console.error("Erreur lors la tentivate de modification du parc", error);
				}
			);
		} else {
			// Cas de la creation d'un nouveau parc
			let parcBase = this.parc as ParcBase;
			let order = {...parcBase} as CreateParcOrder;
			
			// Retrait de l'attribut id issue de this.parc
			delete (order as any).id;

			if (order.energie !== 'Solaire') {
				order.typeOnduleur = null;
				order.hyperviseur = null;
			}
			
			order.indicateursCassini = this.buildIndicateursOrder();
			this.waitingForServer = true;
			this.parcService.create(order).subscribe(
				(_id) => {
					this.waitingForServer = false;
					this.displaySubject.next(false);
	
					// Signaler l'evenement de modification au parent			
					this.parcsChanged.emit();
				},
				(error: HttpErrorResponse) => {
					this.waitingForServer = false;
					this.dialog.open(MessageDialogComponent, {
						width: '400px',
						data: {
							title: "Erreur lors de la création du parc",
							messages: ['Le serveur a rencontré une erreur imprévue']
						}
					});
					console.error("Erreur lors la tentivate de création d'un parc", error);
				}
			);
		}
	}
	
	private buildIndicateurs(indicateurs: IndicateurCassini[]) {
			let indicateursByCode = new Map<number, IndicateurCassini>(
				indicateurs.map((indicateur) => [indicateur.indicateurCode, indicateur])
			);

			this.indicateursAffiches = EditParcComponent.INDICATEURS_TEMPLATE.map(
				(template) => {
					let data = indicateursByCode.get(template.code);

					let surveille = (data == null || data.actif == null) ? '' : (data.actif ? '1' : '0');
					let couleur = (data == null || data.couleur == null || data.couleur === 'NA') ? '' : data.couleur;

					return {
						...template,
						id: data?.id ?? null,
						indicateurId: data?.indicateurId ?? null,
						surveille: surveille,
						couleur: couleur,
						regle: data?.regle ?? '',
					} as IndicateurFormData;
				}
			);
		}
		
		private buildIndicateursOrder(): ParcIndicateurOrder[] {
				return this.indicateursAffiches.map(
					(indicateur) => ({
						indicateurCode: indicateur.code,
						indicateurNom: indicateur.nom,
						couleur: indicateur.couleur ? indicateur.couleur : 'NA',
						regle: indicateur.regle ? indicateur.regle : null,
						actif: (!indicateur.surveillable || indicateur.surveille === '')
							? null
							: Number(indicateur.surveille),
					} as ParcIndicateurOrder)
				);
			}

	onClose() {
		this.intervenantsInternesOptions = null;
		this.responsable$.next(null);
		this.candidatsCac = null;
		this.cac$.next(null);
		this.indicateursAffiches = [];
	}
}